import { useEffect, useState } from "react";
import { ANTHROPIC_MODELS, DEFAULT_MODEL, MODELS, OPENAI_MODELS } from "../../shared/models";
import { REVIEW_TEMPLATES } from "../../shared/review-templates";
import { ADMIN_LOGIN_EMAIL, type AgentAccessMode, type AgentDTO, type AgentOwnerDTO, type AgentOwnerMode, type ConnectorDTO, type UserDTO } from "../../shared/types";
import { del, get, patch, post } from "../lib/api";
import { useScopeTypes } from "../lib/scopes";
import { BrandIcon } from "./BrandIcon";
import { confirmDialog, Dialog } from "./Dialog";
import { MultiSelect } from "./MultiSelect";
import { TextLink } from "./TextLink";
import { Button, Choice, ErrorText, Field, inputCls } from "./ui";
import { Switch } from "./Switch";

/** What a person edits; `scoped` is derived from the scope types, so it is never sent. */
export type AgentDraft = Omit<AgentDTO, "id" | "version" | "defaultScopeId" | "scoped" | "avatarUrl" | "archivedAt" | "createdAt" | "updatedAt"> & { id?: string };

export const EMPTY_AGENT: AgentDraft = {
  slug: "", name: "", description: "", systemPrompt: "", model: DEFAULT_MODEL,
  isPrimary: false, enabled: true, sandboxAllowedDomains: [], connectorIds: [], reviewTemplates: [],
  parentAgentId: null, ownerMode: "all_admins", owners: [], ownerIds: [], accessMode: "all_admins", access: [], monthlyBudgetUsd: null, scopeTypes: [], canDelegate: true, modelPerTask: true, peopleCanAddSkills: false,
  currentVersionId: null, connectorLevels: {},
};

/** The editable part of an agent, without what the server works out for itself. */
export const toDraft = ({ version: _v, defaultScopeId: _d, scoped: _s, avatarUrl: _a, createdAt: _c, updatedAt: _u, ...draft }: AgentDTO): AgentDraft => draft;

/** People and agents for the owner and parent pickers. */
export function usePickers() {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  useEffect(() => {
    get<UserDTO[]>("/api/users").then(setUsers, () => {});
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
  }, []);
  return { users, agents };
}

export const connectorBrand = (c: ConnectorDTO) => (typeof c.authConfig.catalog === "string" ? c.authConfig.catalog : c.slug);

const CUSTOM = "__custom__";

function ModelSelect({ value, onChange, disabled = false }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const known = MODELS.some((m) => m.id === value);
  const [custom, setCustom] = useState(!known);
  const description = MODELS.find((m) => m.id === value)?.description;
  return (
    <div className="space-y-2">
      <select
        className={inputCls}
        disabled={disabled}
        value={custom ? CUSTOM : value}
        onChange={(e) => {
          if (e.target.value === CUSTOM) setCustom(true);
          else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        <optgroup label="Anthropic models">
          {ANTHROPIC_MODELS.filter((m) => !m.label.includes("(alias)")).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label} — {m.description}
            </option>
          ))}
        </optgroup>
        <optgroup label="Claude CLI aliases">
          {ANTHROPIC_MODELS.filter((m) => m.label.includes("(alias)")).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="OpenAI models (Codex)">
          {OPENAI_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label} — {m.description}
            </option>
          ))}
        </optgroup>
        <option value={CUSTOM}>Custom model ID…</option>
      </select>
      {custom ? (
        <input className={`${inputCls} font-mono`} value={value} disabled={disabled} placeholder="claude-… or gpt-…" onChange={(e) => onChange(e.target.value)} autoFocus />
      ) : (
        <p className="truncate font-mono text-xs text-muted">
          {value}
          {description ? ` · ${description}` : ""}
        </p>
      )}
    </div>
  );
}

/** The one scope type an agent works per (094): it works under, and may create, scopes of it only. None: it works on its own. */
function ScopeTypesField({ value, onChange }: { value: string[]; onChange: (keys: string[]) => void }) {
  const { types } = useScopeTypes();
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-muted">Scope type</div>
      {types?.length === 0 ? (
        <span className="text-xs text-faint">
          No scope types yet: make one on the <TextLink to="/admin/scopes">Scopes</TextLink> page.
        </span>
      ) : (
        <Choice
          label="Scope type"
          value={value[0] ?? ""}
          options={[{ value: "", label: "None" }, ...(types ?? []).map((t) => ({ value: t.key, label: t.name }))]}
          onChange={(key) => onChange(key ? [key] : [])}
        />
      )}
      <p className="mt-1.5 text-xs text-muted">It works per one scope type: under, and creating, scopes of it only. With none, it always works on its own.</p>
    </div>
  );
}

/** People picked several at a time from a dropdown: an agent's named owners (the last one stays) or who can use it. */
function People({ picked, people, keepOne, placeholder, onChange }: {
  picked: AgentOwnerDTO[]; people: UserDTO[]; keepOne: boolean; placeholder: string; onChange: (o: AgentOwnerDTO[]) => void;
}) {
  const last = keepOne && picked.length === 1 ? picked[0]!.userId : null;
  return (
    <MultiSelect
      options={people.map((u) => ({ value: u.id, label: u.name, locked: u.id === last, hint: u.id === last ? "keeps one owner" : u.email }))}
      selected={picked.map((o) => o.userId)}
      onChange={(ids) => onChange(ids.map((id) => ({ userId: id, name: people.find((u) => u.id === id)?.name ?? picked.find((o) => o.userId === id)?.name ?? "" })))}
      placeholder={placeholder}
      wide
    />
  );
}

/** Who answers for an agent, in one line: "Owners: all admins", "Owner: Nikos Tzamos". */
export const ownersLine = (a: Pick<AgentDTO, "ownerMode" | "owners">) =>
  // A named list with nobody alive on it reads as all admins, as the platform answers it.
  a.ownerMode === "all_admins" || a.owners.length === 0 ? "Owners: all admins" : `Owner${a.owners.length === 1 ? "" : "s"}: ${a.owners.map((o) => o.name).join(", ")}`;

/** Who may use an agent, in a few words: its owners are always included. */
export const ACCESS_LINE: Record<AgentAccessMode, string> = { named: "Used by named people", all_admins: "Used by admins", everyone: "Used by everyone" };

/** Who can use it, on a line: the mode, and the people named on top of it, who count in every mode (AIT-105). */
export function accessLine(a: Pick<AgentDTO, "accessMode" | "access">): string {
  const extra = a.accessMode !== "named" && a.access.length ? ` and ${a.access.length} named` : "";
  return `${ACCESS_LINE[a.accessMode]}${extra}`;
}

const OWNER_CHOICES: { value: AgentOwnerMode; label: string }[] = [{ value: "named", label: "Named admins" }, { value: "all_admins", label: "All admins" }];
const ACCESS_CHOICES: { value: AgentAccessMode; label: string }[] = [
  { value: "named", label: "Named people" }, { value: "all_admins", label: "All admins" }, { value: "everyone", label: "Everyone" },
];
const ACCESS_MEANS: Record<AgentAccessMode, string> = {
  named: "Its owners and the people named here.",
  all_admins: "Its owners, every admin, now and later, and the people named here.",
  everyone: "Everyone on the platform.",
};

/**
 * Who answers for an agent and who may use it (D5). Any admin sets both; the owners are who may change its
 * instructions, skills for everyone, shared memory and goals, and who decide what it asks for.
 */
function OwnersAndAccess({ draft, users, set }: { draft: AgentDraft; users: UserDTO[]; set: <K extends keyof AgentDraft>(k: K, v: AgentDraft[K]) => void }) {
  const people = users.filter((u) => u.kind === "person" && !u.retiredAt);
  const admins = people.filter((u) => u.role === "admin" && u.email !== ADMIN_LOGIN_EMAIL);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Owners" hint="They answer for it: its instructions, its skills for everyone, its shared memory and goals, and what it asks to do. Any one of them decides.">
        <div className="space-y-2">
          <Choice label="Owners" value={draft.ownerMode} options={OWNER_CHOICES} onChange={(v) => set("ownerMode", v)} />
          {draft.ownerMode === "named" ? (
            <People picked={draft.owners} people={admins} keepOne placeholder="Name at least one admin" onChange={(o) => set("owners", o)} />
          ) : (
            <p className="text-xs text-muted">Every admin, now and later.</p>
          )}
        </div>
      </Field>
      <Field label="Who can use it" hint="Its owners always can. A member reaches it through Pandito; an admin also talks to it directly, from the chat.">
        {draft.isPrimary ? (
          // Every member reaches every agent through it: the server keeps it open to everyone, so nothing is offered here.
          <p className="text-xs text-muted">Everyone, always: every member reaches every agent through {draft.name || "it"}.</p>
        ) : (
          <div className="space-y-2">
            <Choice label="Who can use it" value={draft.accessMode} options={ACCESS_CHOICES} onChange={(v) => set("accessMode", v)} />
            {/* People named here can use it whichever is chosen above (AIT-105). */}
            <People picked={draft.access} people={people} keepOne={false} placeholder={draft.accessMode === "named" ? "Nobody but its owners" : "Nobody named"} onChange={(o) => set("access", o)} />
            <p className="text-xs text-muted">{ACCESS_MEANS[draft.accessMode]}</p>
          </div>
        )}
      </Field>
    </div>
  );
}

/** What every run of an agent reads of its description and its instructions before a warning shows (AIT-27). Never refused. */
export const DESCRIPTION_BUDGET = 400;
export const INSTRUCTIONS_BUDGET = 8000;

/**
 * Name, description, instructions, model and connectors of an agent: an agent is written in four boxes, its goal the
 * fourth, on the Goal tab (AIT-27). The primary agent always has every connector. Its instructions and model are its
 * owners' (D5): locked for any other admin on an agent that exists, open on a new one.
 */
export function AgentForm({ draft, connectors, onChange, users = [], agents = [] }: {
  draft: AgentDraft; connectors: ConnectorDTO[]; onChange: (d: AgentDraft) => void; users?: UserDTO[]; agents?: AgentDTO[];
}) {
  const set = <K extends keyof AgentDraft>(k: K, v: AgentDraft[K]) => onChange({ ...draft, [k]: v });
  const operates = !draft.id || draft.youAnswerFor !== false;
  const enabledConnectors = connectors.filter((c) => c.enabled);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name">
          <input className={inputCls} value={draft.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Slug">
          <input className={`${inputCls} font-mono`} value={draft.slug} onChange={(e) => set("slug", e.target.value)} placeholder="ops-analyst" />
        </Field>
      </div>
      <Field label="Description" hint="What it does and when to hand it work, in one or two sentences.">
        <textarea className={inputCls} rows={2} value={draft.description} onChange={(e) => set("description", e.target.value)} />
        <OverBudget chars={draft.description.length} budget={DESCRIPTION_BUDGET} what="Every run reads it, and other agents choose it by its first line: keep it to one or two sentences." />
      </Field>
      <OwnersAndAccess draft={draft} users={users} set={set} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Monthly budget (USD)" hint="New runs stop once this month's spend reaches it. Empty means no cap.">
          <input
            className={inputCls}
            type="number"
            min={0}
            step={1}
            value={draft.monthlyBudgetUsd ?? ""}
            placeholder="No cap"
            onChange={(e) => set("monthlyBudgetUsd", e.target.value === "" ? null : Number(e.target.value))}
          />
        </Field>
      </div>
      {!draft.isPrimary && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Parent agent" hint="A sub-agent works with its parent's connectors, and its prompt names its parent.">
            <select
              className={inputCls}
              value={draft.parentAgentId ?? ""}
              disabled={!operates}
              onChange={(e) => {
                const parent = agents.find((a) => a.id === e.target.value);
                // A new sub-agent starts with its parent's owners, as the platform makes one; an existing agent keeps its own.
                onChange({ ...draft, parentAgentId: parent?.id ?? null, ...(!draft.id && parent ? { ownerMode: parent.ownerMode, owners: parent.owners } : {}) });
              }}
            >
              <option value="">None (top-level specialist)</option>
              {agents
                // An archived agent is offered as nobody's new parent, but stays shown as the one already chosen.
                .filter((a) => !a.isPrimary && a.id !== draft.id && (!a.archivedAt || a.id === draft.parentAgentId))
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
      )}
      {!operates && (
        <div className="rounded-xl bg-fill-subtle px-3 py-2.5 text-sm text-muted">
          Its instructions, model and parent are for its owners to change. Any admin sets its settings; to change these, add yourself as an owner first.
        </div>
      )}
      <Field label="Instructions" hint="The job, the rules with their reasons, the finish line. Reference facts go at the end under ## Reference.">
        <textarea className={`${inputCls} font-mono text-xs`} rows={12} value={draft.systemPrompt} disabled={!operates} onChange={(e) => set("systemPrompt", e.target.value)} />
        <span className="mt-1 block text-tiny text-faint">{draft.systemPrompt.length.toLocaleString("en-US")} / {INSTRUCTIONS_BUDGET.toLocaleString("en-US")} characters</span>
        <OverBudget chars={draft.systemPrompt.length} budget={INSTRUCTIONS_BUDGET} what="Long reference and step-by-step procedures belong in skills, and what a tool needs in its connector's guide, which every agent using it shares." />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label="Default model"
          hint="Used whenever nobody chooses another: chatting with it, its schedules, its own wakes. When it is handed a task, Pandito may choose another model for that task."
        >
          <ModelSelect key={draft.id ?? "new"} value={draft.model} disabled={!operates} onChange={(v) => set("model", v)} />
        </Field>
        <Field label="Extra allowed domains" hint="Comma separated (informational).">
          <input
            className={inputCls}
            value={draft.sandboxAllowedDomains.join(", ")}
            onChange={(e) => set("sandboxAllowedDomains", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
        </Field>
      </div>
      {!draft.isPrimary && (
        <Switch
          label="Pandito may choose its model per task"
          hint="On, an agent handing it a task picks the model for that task: a lighter one for a plain fetch, a heavier one for deep judgment, up to Opus 5.5. Off, every task runs on its default model."
          checked={draft.modelPerTask}
          disabled={!operates}
          onChange={(v) => set("modelPerTask", v)}
        />
      )}
      {!draft.isPrimary && <ScopeTypesField value={draft.scopeTypes} onChange={(keys) => set("scopeTypes", keys)} />}
      <Switch
        label="Delegate outward"
        hint="On, it may hand work to other agents, only ever to the ones the person it works for may use. Off, its runs are never offered delegation and are refused if they try."
        checked={draft.canDelegate}
        onChange={(v) => set("canDelegate", v)}
      />
      <Switch
        label="People can add their own skills"
        hint="On, a person it works for can write a new skill of their own here, which only their own runs see. Off, nobody writes one here; a skill of theirs shared with it from another agent still applies, and the ones already written stay exactly as they are."
        checked={draft.peopleCanAddSkills}
        onChange={(v) => set("peopleCanAddSkills", v)}
      />
      {draft.isPrimary ? (
        <div className="rounded-xl bg-haze-soft px-3 py-2.5 text-sm text-slate dark:bg-haze/10 dark:text-haze">
          Pandito is the primary agent: always enabled and used by everyone. It can use every connector except the ones that need a grant of their own (WorkOS People goes to the People Manager alone); for each person it only reaches the connectors that person has in Users.
        </div>
      ) : (
        <>
          <Field label="Connectors" hint="What this agent can use. For each request it only reaches the connectors the person who asked has.">
            <div className="flex flex-wrap gap-2">
              {enabledConnectors.map((c) => {
                const on = draft.connectorIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set("connectorIds", on ? draft.connectorIds.filter((x) => x !== c.id) : [...draft.connectorIds, c.id])}
                    className={`inline-flex items-center gap-2 rounded-full py-1 pr-3 pl-1 text-sm ring-1 transition ${on ? "bg-ink text-paper ring-ink dark:bg-white dark:text-ink dark:ring-white" : "bg-surface text-muted ring-line hover:bg-fill-subtle"}`}
                  >
                    <span className="grid size-6 place-items-center rounded-full bg-white">
                      <BrandIcon brand={connectorBrand(c)} className="size-4" />
                    </span>
                    {c.name}
                  </button>
                );
              })}
              {enabledConnectors.length === 0 && <span className="text-xs text-faint">No connectors yet.</span>}
            </div>
          </Field>
        </>
      )}
      <Field
        label="Request templates"
        hint="What this agent may put in front of a person for a verdict before it is used. It can file only what is ticked here; the three verdicts, the page and what approving does belong to the template."
      >
        <div className="flex flex-wrap gap-2">
          {REVIEW_TEMPLATES.map((t) => {
            const on = draft.reviewTemplates.includes(t.name);
            return (
              <button
                key={t.name}
                type="button"
                aria-pressed={on}
                title={t.when}
                onClick={() => set("reviewTemplates", on ? draft.reviewTemplates.filter((x) => x !== t.name) : [...draft.reviewTemplates, t.name])}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm ring-1 transition ${on ? "bg-ink text-paper ring-ink dark:bg-white dark:text-ink dark:ring-white" : "bg-surface text-muted ring-line hover:bg-fill-subtle"}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

export async function saveAgent(draft: AgentDraft): Promise<AgentDTO> {
  const { id, currentVersionId, connectorLevels: _levels, owners, ownerIds: _ids, access, youAnswerFor: _owns, youCanUse: _uses, ...rest } = draft;
  // Owners and who can use it are sets of people, written like the connectors: the whole list, by id, with their mode.
  const body = { ...rest, ownerUserIds: owners.map((o) => o.userId), accessUserIds: access.map((a) => a.userId) };
  if (!body.model.trim()) throw new Error("Choose a model");
  // The save names the version it was edited from, so a stale form can never overwrite a newer edit.
  return id ? patch<AgentDTO>(`/api/agents/${id}`, { ...body, baseVersionId: currentVersionId }) : post<AgentDTO>("/api/agents", body);
}

export async function deleteAgent(agent: { id: string; name: string }): Promise<boolean> {
  if (!(await confirmDialog({ title: `Delete ${agent.name}?`, body: "Only an agent that has never run can be deleted, and it cannot be undone. One that has run is archived instead.", confirm: "Delete agent", danger: true }))) return false;
  await del(`/api/agents/${agent.id}`);
  return true;
}

/** New agent dialog. */
export function NewAgentModal({ connectors, onClose, onCreated, preset, title = "New agent" }: {
  connectors: ConnectorDTO[]; onClose: () => void; onCreated: (a: AgentDTO) => void; preset?: Partial<AgentDraft>; title?: string;
}) {
  const [draft, setDraft] = useState<AgentDraft>({ ...EMPTY_AGENT, ...preset });
  const { users, agents } = usePickers();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={title}
      busy={saving}
      primary={
        <Button
            variant="primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setError(null);
              try {
                onCreated(await saveAgent(draft));
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setSaving(false);
              }
            }}
          >
          {saving ? "Creating…" : "Create agent"}
        </Button>
      }
    >
      <AgentForm draft={draft} connectors={connectors} onChange={setDraft} users={users} agents={agents} />
      <div className="mt-3">
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

/** A warning, never a refusal, once a box holds more than every run should read. */
export function OverBudget({ chars, budget, what }: { chars: number; budget: number; what: string }) {
  if (chars <= budget) return null;
  return (
    <span className="mt-1 block text-xs text-warning">
      {chars.toLocaleString("en-US")} characters, over the {budget.toLocaleString("en-US")} every run should read. {what}
    </span>
  );
}
